import { useCreateShipmentFormContext } from "inbounds/createShipment/useCreateShipmentFormContext";
import { CreateShipmentInputName } from "inbounds/createShipment/useCreateShipmentForm";
import { useEffect, useMemo, useCallback, useRef } from "react";
import { batch, useSelector } from "react-redux";
import { RootState } from "RootReducer";

export const useSyncFormWithState = (isDtcMixedSkuBooking?: boolean, index?: number) => {
  const { setValue, getValues } = useCreateShipmentFormContext();
  const packageIndex = index ?? 0;

  // Use ref to prevent infinite loops
  const previousValuesRef = useRef<{
    formattedItemsHash?: string;
    packageDimensionsHash?: string;
  }>({});

  const getPackagePath = useCallback(
    (field: string) => `${CreateShipmentInputName.MIXED_SKU_PACKAGES}.${packageIndex}.${field}` as any,
    [packageIndex]
  );
  const itemsPath = useMemo(() => getPackagePath("items"), [getPackagePath]);

  // Use single selector to avoid race conditions
  const storageState = useSelector((state: RootState) => {
    const storageCreate = state.storageInboundCreate;
    return {
      selectedProducts: storageCreate?.selectedProducts ?? {},
      selectedProductsCaseDimensions: storageCreate?.selectedProductsCaseDimensions ?? {},
    };
  });

  // Get current items without validation
  const currentItems = useMemo(() => {
    if (!isDtcMixedSkuBooking) {
      return [];
    }

    try {
      const rawItems = getValues(itemsPath) ?? [];
      return Array.isArray(rawItems) ? rawItems : [];
    } catch {
      return [];
    }
  }, [getValues, itemsPath, isDtcMixedSkuBooking]);

  // Get package dimensions
  const packageDimensions = useMemo(() => {
    if (!isDtcMixedSkuBooking || currentItems.length === 0) {
      return undefined;
    }

    try {
      const firstItem = currentItems[0];
      if (!firstItem?.dsku) {
        return undefined;
      }

      const dimensions = storageState.selectedProductsCaseDimensions[firstItem.dsku];
      return dimensions && typeof dimensions === "object" ? dimensions : undefined;
    } catch {
      return undefined;
    }
  }, [isDtcMixedSkuBooking, currentItems, storageState.selectedProductsCaseDimensions]);

  // Memoize current items as a Set for O(1) lookup
  const currentItemsDskuSet = useMemo(() => {
    try {
      const dskus = currentItems.map((item: any) => item.dsku).filter(Boolean);
      return new Set(dskus);
    } catch {
      return new Set<string>();
    }
  }, [currentItems]);

  // Calculate formatted items without extensive validation
  const formattedItems = useMemo(() => {
    if (!isDtcMixedSkuBooking || !storageState.selectedProducts) {
      return [];
    }

    try {
      const productEntries = Object.entries(storageState.selectedProducts);

      return productEntries
        .filter(([dsku, product]) => {
          return dsku && currentItemsDskuSet.has(dsku) && product && typeof product === "object";
        })
        .map(([dsku, product]) => ({
          dsku,
          qty: product.qty ?? 0,
        }));
    } catch {
      return [];
    }
  }, [isDtcMixedSkuBooking, storageState.selectedProducts, currentItemsDskuSet]);

  // Safe comparison with loop prevention
  const needsUpdate = useMemo(() => {
    if (!isDtcMixedSkuBooking || formattedItems.length === 0) {
      return false;
    }

    try {
      // Check against previous hash to prevent loops
      const currentHash = JSON.stringify(formattedItems);
      if (previousValuesRef.current.formattedItemsHash === currentHash) {
        return false;
      }

      if (formattedItems.length !== currentItems.length) {
        return true;
      }

      return formattedItems.some((item, idx) => {
        const currentItem = currentItems[idx];
        return !currentItem || item.dsku !== currentItem.dsku || item.qty !== currentItem.qty;
      });
    } catch {
      return false;
    }
  }, [isDtcMixedSkuBooking, formattedItems, currentItems]);

  // Safe form update with error handling and loop prevention
  useEffect(() => {
    if (!isDtcMixedSkuBooking || !storageState.selectedProducts || !needsUpdate || formattedItems.length === 0) {
      return;
    }

    try {
      const currentHash = JSON.stringify(formattedItems);
      previousValuesRef.current.formattedItemsHash = currentHash;

      setValue(itemsPath, formattedItems);
    } catch {
      // Reset hash on error to allow retry
      previousValuesRef.current.formattedItemsHash = undefined;
    }
  }, [needsUpdate, formattedItems, isDtcMixedSkuBooking, storageState.selectedProducts, itemsPath, setValue]);

  // Safe package dimensions update
  useEffect(() => {
    if (!isDtcMixedSkuBooking || !packageDimensions) {
      return;
    }

    try {
      // Check against previous hash to prevent unnecessary updates
      const currentHash = JSON.stringify(packageDimensions);
      if (previousValuesRef.current.packageDimensionsHash === currentHash) {
        return;
      }

      const { weight, length, width, height } = packageDimensions;

      // Batch all package dimension updates to reduce form re-renders
      batch(() => {
        try {
          if (length) {
            setValue(getPackagePath("length"), length);
          }
          if (width) {
            setValue(getPackagePath("width"), width);
          }
          if (height) {
            setValue(getPackagePath("height"), height);
          }
          setValue(getPackagePath("weight"), weight ?? 0);

          // Safe numberOfPkgs handling
          try {
            const currentNumberOfPkgs = getValues(getPackagePath("numberOfPkgs"));
            if (!currentNumberOfPkgs) {
              setValue(getPackagePath("numberOfPkgs"), 1);
            }
          } catch {
            setValue(getPackagePath("numberOfPkgs"), 1);
          }

          // Update hash after successful update
          previousValuesRef.current.packageDimensionsHash = currentHash;
        } catch {
          // Reset hash on batch error
          previousValuesRef.current.packageDimensionsHash = undefined;
        }
      });
    } catch {
      // Reset hash on any error
      previousValuesRef.current.packageDimensionsHash = undefined;
    }
  }, [packageDimensions, isDtcMixedSkuBooking, packageIndex, setValue, getValues, getPackagePath]);

  return {
    packageDimensions,
    intlDtcProducts: storageState.selectedProducts,
  };
};
